/*----------------------------------
- TLD PAGE: NAME SAMPLE
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @fix  docs/fixes/2026-10-17-tld-sample-nan-price.md
 * @rule One bounded sample (ADR-0028 amendment): at most `TLD_INSIGHT_SAMPLE_LIMIT` names, in
 *       the listing's own order, with three fields (name, status, price) and nothing else. A
 *       table, its column headers for screen readers only: the card's rows read without them.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { formatTldUsdOr } from '@uniquedomains/components/contracts/tlds/format';
import {
    TLD_INSIGHT_SAMPLE_LIMIT,
    formatTldLabel,
    type PublicTldSampleRow,
} from '@uniquedomains/components/contracts/tlds/public';
import {
    resolveDomainStatusDotHex,
    resolveDomainStatusRowLabel,
} from '@uniquedomains/components/contracts/domains/statusMixCatalog';
import { TLD_PAGE_CARD_ICONS } from '@uniquedomains/components/tlds/page/pageCatalog';
import TldPageCard from '@uniquedomains/components/tlds/page/TldPageCard';

import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldSampleSection({ slug, sample }: { slug: string; sample: readonly PublicTldSampleRow[] }) {
    const content = TLD_PAGE_CONTENT.insight.sample;
    // Product already caps it; slicing again keeps the page to its contract whatever arrives.
    const rows = sample.slice(0, TLD_INSIGHT_SAMPLE_LIMIT);

    return (
        <TldPageCard
            tone="white"
            icon={TLD_PAGE_CARD_ICONS.sample}
            title={content.heading}
            testId="tld-page-sample"
            empty={rows.length === 0 ? content.empty : null}
        >
            <table class="mt-3.5 w-full border-collapse text-left text-[13.5px] tabular-nums">
                <caption class="sr-only">{content.caption(formatTldLabel(slug))}</caption>
                <thead class="sr-only">
                    <tr>
                        <th scope="col">{content.domain}</th>
                        <th scope="col">{content.status}</th>
                        <th scope="col">{content.price}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.domain} class="h-[38px] border-t border-[rgba(27,29,30,0.05)]" data-testid="tld-page-sample-row">
                            <th scope="row" class="max-w-0 truncate pr-2 font-semibold">{row.domain}</th>
                            <td class="w-[110px] whitespace-nowrap pr-2 text-[12.5px] text-[var(--ud-copy-soft)]">
                                <span
                                    aria-hidden="true"
                                    class="mr-1.5 inline-block size-[7px] rounded-full align-middle"
                                    style={{ backgroundColor: resolveDomainStatusDotHex(row.status) }}
                                />
                                {resolveDomainStatusRowLabel(row.status) ?? row.status}
                            </td>
                            <td class="w-[90px] text-right font-bold">{formatTldUsdOr(row.price, content.noPrice)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </TldPageCard>
    );
}
